import { format } from '@/i18n/format'
import type { LocaleCode } from '@/i18n/locales'
import type { Profile, SlotId, SlotSource } from '@/profile/types'
import { makeSlot } from '@/profile/types'
import type { Extraction } from '@/voice/nlu/extract'
import { extractAffirmation, extractName, extractRegion } from '@/voice/nlu/extract'
import type { OnboardingScript } from './script'
import { BILINGUAL_OPEN } from './script'

/**
 * The onboarding conversation, as data.
 *
 * Each step knows four things and nothing else: what to ask, how to read an
 * answer, where to put it, and how to say it back. That shape is carried over
 * from the implementation this replaces, because it was right: it is what lets
 * `engine.ts` be a state machine over a list rather than a function with a
 * branch per question, and it is what lets the whole conversation be asserted
 * without rendering anything.
 *
 * What changed is the order, and the order is the design. The old flow asked
 * five questions before the person got anything, which is a form with a voice.
 * Here a stranger earns the right to ask by being useful first: language,
 * consent, name, then the product, and only then place.
 *
 * Age band and gender are absent by intent rather than by omission. See
 * `volunteered.ts` for what happens when somebody offers one anyway.
 */

export type OnboardingStepId = 'language' | 'consent' | 'name' | 'open' | 'place'

export interface StepContext {
  script: OnboardingScript
  locale: LocaleCode
  /** Rotates phrasings, so a step asked twice does not repeat itself. */
  rotate(key: string, variants: readonly string[]): string
  profile: Profile
}

/**
 * When a value is said back before it is kept.
 *
 * `always` exists for exactly one step and is the point of this whole file.
 * See `nameStep`.
 */
export type ConfirmPolicy = 'never' | 'when-unsure' | 'always'

export interface OnboardingStep {
  id: OnboardingStepId
  /** Where the answer lands, or null for the step that asks for nothing. */
  slot: SlotId | null
  confirm: ConfirmPolicy
  /**
   * Whether the step listens at all. False for the open conversation, which
   * hands the microphone to the assistant rather than parsing a reply itself.
   */
  listens: boolean
  ask(context: StepContext): string
  /**
   * Reads an answer.
   *
   * Absent on two steps, and in both cases because there is nothing for a
   * parser to return. The open conversation asks for nothing. The language
   * step's answer is read by `readReplyLocale` in the engine instead, because
   * its result also decides the `SlotSource`, which an `Extraction` cannot
   * carry: a language somebody named out loud is `chosen`, a language read
   * from the words they used is `spoken`, and the device's own language is
   * `inferred`, and those three deserve different treatment later.
   */
  parse?(transcript: string, locale: LocaleCode): Extraction<unknown>
  /** How the value is said back before it is kept. */
  readBack?(context: StepContext, spoken: string): string
  /** What to say when a read-back was rejected, before asking again. */
  reask?(context: StepContext): string
  commit(
    draft: Profile,
    value: unknown,
    source: SlotSource,
    confidence: number,
    raw: string,
    now: number,
  ): void
  /** Renders a value for the read-back and for the acknowledgement. */
  describe(value: unknown): string
}

/** Keeps the casts in one place so each definition below stays fully typed. */
function defineStep<T>(step: {
  id: OnboardingStepId
  slot: SlotId | null
  confirm?: ConfirmPolicy
  listens?: boolean
  ask: (context: StepContext) => string
  parse?: (transcript: string, locale: LocaleCode) => Extraction<T>
  readBack?: (context: StepContext, spoken: string) => string
  reask?: (context: StepContext) => string
  commit: (draft: Profile, slot: ReturnType<typeof makeSlot<T>>) => void
  describe: (value: T) => string
}): OnboardingStep {
  const parse = step.parse
  return {
    id: step.id,
    slot: step.slot,
    confirm: step.confirm ?? 'never',
    listens: step.listens ?? true,
    ask: step.ask,
    ...(parse === undefined
      ? {}
      : { parse: (transcript: string, locale: LocaleCode) => parse(transcript, locale) as Extraction<unknown> }),
    ...(step.readBack === undefined ? {} : { readBack: step.readBack }),
    ...(step.reask === undefined ? {} : { reask: step.reask }),
    commit: (draft, value, source, confidence, raw, now) => {
      step.commit(draft, makeSlot(value as T, source, confidence, raw, now))
    },
    describe: (value) => step.describe(value as T),
  }
}

/**
 * The bilingual open.
 *
 * There is no question here and there is no menu. Namu greets in both
 * languages, and whatever comes back decides. The step exists as a step, with
 * a slot and a parser like any other, so that the engine does not need a
 * special case for the first thing that happens.
 */
const languageStep = defineStep<LocaleCode>({
  id: 'language',
  slot: 'language',
  ask: () => BILINGUAL_OPEN,
  commit: (draft, slot) => {
    draft.language = slot
  },
  describe: (value) => value,
})

/**
 * One consent, not three.
 *
 * Whether Namu may keep what is said, so it can remember somebody between
 * conversations. Training on a voice is a separate grant, it defaults to off,
 * and it lives in Settings where it can be read rather than heard once and
 * agreed to in passing. That is not bundling avoided by asking three
 * questions; it is not asking, and defaulting to the answer that costs the
 * person nothing.
 *
 * The four points are spoken before the question rather than only shown. A
 * spoken yes is a valid signature but a spoken disclosure is not a valid
 * record, and a written disclosure nobody reads aloud is no disclosure at all
 * to somebody using this by voice or with a screen reader.
 */
const consentStep = defineStep<boolean>({
  id: 'consent',
  slot: 'consent',
  ask: ({ script, rotate }) =>
    `${script.consentPoints.join(' ')} ${rotate('consentAsk', script.consentAsk)}`,
  parse: (transcript, locale) => {
    const answer = extractAffirmation(transcript, locale)
    /*
     * Only an explicit yes or a no counts. A hesitation is not a refusal, and
     * collapsing "I do not know" into a decline records a decision the person
     * did not make, permanently, in the one place where being wrong costs the
     * most. Returning nothing re-asks.
     */
    if (answer.value !== 'yes' && answer.value !== 'no') {
      return { value: null, confidence: 0, raw: answer.raw }
    }
    return { value: answer.value === 'yes', confidence: answer.confidence, raw: answer.raw }
  },
  commit: (draft, slot) => {
    draft.consent = slot
  },
  describe: (value) => (value ? 'yes' : 'no'),
})

/**
 * The name, always read back.
 *
 * `when-unsure` would be the consistent choice and it is the wrong one here.
 * Confidence is the product of a recogniser's certainty and a parser's, and
 * neither of them knows anything about Hausa names: a recogniser trained
 * mostly on English returns a high number for a confident mishearing, so
 * "Aminata" comes back as "Amina" at 0.9 and is kept without a word. Somebody
 * greeted for a year as the wrong name will not correct it, because after the
 * second week correcting it is rude.
 *
 * So it is said back every time it is heard. A typed name is exempt, since the
 * person saw exactly what they gave and reading it back is theatre.
 */
const nameStep = defineStep<string>({
  id: 'name',
  slot: 'name',
  confirm: 'always',
  ask: ({ script, rotate }) => rotate('askName', script.askName),
  parse: (transcript, locale) => extractName(transcript, locale),
  readBack: ({ script, rotate }, spoken) =>
    format(rotate('confirmName', script.confirmName), { name: spoken }),
  // An apology, not a repetition of the question. Being told "what should I
  // call you" a second time reads as though the first answer was not given,
  // when in fact it was given and got mangled.
  reask: ({ script, rotate }) => rotate('correctName', script.correctName),
  commit: (draft, slot) => {
    draft.name = slot
  },
  describe: (value) => value,
})

/**
 * Where the product happens.
 *
 * The centre of the design, and the reason it is a step at all rather than
 * something after onboarding: place is asked once this has been useful, so the
 * flow has to know when that was. The engine leaves the microphone to the
 * assistant here and waits to be told a question was answered.
 *
 * The invitation carries the name because this is the sentence immediately
 * after the name was settled, and using it there is worth more than any
 * explanation of what Namu does.
 */
const openStep = defineStep<never>({
  id: 'open',
  slot: null,
  listens: false,
  ask: ({ script, rotate, profile }) => {
    const name = profile.name?.value
    return name === undefined || name === ''
      ? rotate('invite', script.invite)
      : format(rotate('inviteNamed', script.inviteNamed), { name })
  },
  commit: () => {},
  describe: () => '',
})

/**
 * Place, asked afterwards, with the reason attached.
 *
 * The reason is not decoration. "Where do you live" from a stranger who has
 * done nothing for you is a question about you; the same question from
 * something that has just answered a question well, with "what I can tell you
 * about a clinic is different in Kano and in Sokoto" attached, is a question
 * about the answer.
 */
const placeStep = defineStep<string>({
  id: 'place',
  slot: 'region',
  confirm: 'when-unsure',
  ask: ({ script, rotate }) => rotate('askPlace', script.askPlace),
  parse: (transcript, locale) => extractRegion(transcript, locale),
  readBack: ({ script, rotate }, spoken) =>
    format(rotate('confirmHeard', script.confirmHeard), { value: spoken }),
  commit: (draft, slot) => {
    draft.region = slot
  },
  describe: (value) => value,
})

/**
 * The order, which is the whole argument.
 *
 * Language first because nothing else can be said until it is known. Consent
 * second because nothing may be written before it is given. Name third because
 * it is the shortest answer and the one that proves Namu was listening. Then
 * the product. Then, and only then, one more question.
 */
export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  languageStep,
  consentStep,
  nameStep,
  openStep,
  placeStep,
]

export function findStep(id: OnboardingStepId): OnboardingStep {
  const step = ONBOARDING_STEPS.find((candidate) => candidate.id === id)
  if (!step) throw new Error(`unknown onboarding step: ${id}`)
  return step
}
